// src/contexts/ThemeContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [theme, setTheme] = useState('light');

  // Load theme preference when user changes
  useEffect(() => {
    const loadUserTheme = () => {
      if (currentUser) {
        // User-specific theme from localStorage
        const userTheme = localStorage.getItem(`user_theme_${currentUser.uid}`);
        if (userTheme) {
          setTheme(userTheme);
          applyTheme(userTheme);
        } else {
          // Default to light theme for new users
          setTheme('light');
          applyTheme('light');
          localStorage.setItem(`user_theme_${currentUser.uid}`, 'light');
        }
      } else {
        // Non-authenticated users get system preference
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches 
          ? 'dark' : 'light';
        setTheme(systemTheme);
        applyTheme(systemTheme);
      }
    };

    loadUserTheme();
  }, [currentUser]);

  // Apply theme to DOM
  const applyTheme = useCallback((themeToApply) => {
    document.documentElement.setAttribute('data-theme', themeToApply);
    document.body.className = themeToApply;
  }, []);

  // Toggle theme and save preference
  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      
      // Save only for authenticated users
      if (currentUser) {
        localStorage.setItem(`user_theme_${currentUser.uid}`, newTheme);
      }
      
      applyTheme(newTheme);
      return newTheme;
    });
  }, [currentUser, applyTheme]);

  // Clear theme when user logs out
  useEffect(() => {
    if (!currentUser) {
      document.documentElement.removeAttribute('data-theme');
      document.body.className = '';
    }
  }, [currentUser]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};